import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ClientProjectPicker } from "./ClientProjectPicker";
import { PreviewManager } from "./PreviewManager";
import { PromptDialog } from "@/components/PromptDialog";
import {
  Rocket, Target, Loader2, Sparkles, Copy, RefreshCw, CheckCircle2, FileDown,
  Download, Trash2, FolderPlus, Save, Brain, AlertTriangle, Globe, ExternalLink, Send, FolderInput,
  Pencil, X, Undo2, ListChecks, ShieldCheck, ChevronDown,
} from "lucide-react";

const SEL = "w-full bg-navy border border-white/20 text-white p-2.5 text-sm focus:border-gold focus:outline-none";
const LBL = "block text-xs uppercase tracking-wide text-gray-500 mb-1.5";
const slug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const CONTACT_KEYS = [["phone", "Phone number"], ["email", "Email address"], ["business_address", "Business address"]];
const TRUST_FIELDS = [
  ["ownership", "Ownership / structure", "e.g. Family-owned, veteran-owned, locally owned"],
  ["years_in_business", "Years in business", "e.g. 12"],
  ["licensed", "Licensed", "e.g. Yes — AL Master Plumber #12345"],
  ["insured", "Insured", "e.g. Yes — fully insured & bonded"],
  ["certifications", "Certifications", "e.g. EPA, NATE certified"],
  ["warranty", "Warranty / guarantee", "e.g. 1-year labor warranty"],
  ["hours", "Business hours", "e.g. Mon–Fri 8–5, Sat 9–1"],
  ["emergency", "Emergency / same-day", "e.g. 24/7 emergency service"],
  ["free_estimates", "Free estimates", "e.g. Yes — free estimates"],
  ["financing", "Financing", "e.g. Financing available (OAC)"],
  ["service_areas", "Service areas", "e.g. Montgomery, Prattville, Wetumpka"],
];

export function WebsiteBuilder({ config, clients, projects, clientId, setClientId, projectId, setProjectId, reloadClients, reloadProjects }) {
  const blocks = config.website_blocks || [];
  const fullBlocks = useMemo(() => blocks.filter((b) => b.in_full), [blocks]);

  const [f, setF] = useState({ context: "", industry: [], goal: [], audience: [], tone: "Professional", model: "auto", keywords: "", target_city: "", target_state: "", industry_custom: "", audience_custom: "", goal_custom: "", brand_color: "#C9A227" });
  const CUSTOM = "Other (Custom)";
  const isCustom = (v) => typeof v === "string" && (v === CUSTOM || /\(custom\)/i.test(v));
  const toggleGoal = (g) => setF((p) => ({ ...p, goal: p.goal.includes(g) ? p.goal.filter((x) => x !== g) : [...p.goal, g] }));
  const toggleArr = (key, v) => setF((p) => ({ ...p, [key]: p[key].includes(v) ? p[key].filter((x) => x !== v) : [...p[key], v] }));
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [mode, setMode] = useState("full");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [genMeta, setGenMeta] = useState(null);
  const [preview, setPreview] = useState(null);
  const [previewToken, setPreviewToken] = useState(null);
  const [pvBusy, setPvBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [emailPrompt, setEmailPrompt] = useState(false);
  const [convertBusy, setConvertBusy] = useState(false);
  const [genProjectId, setGenProjectId] = useState(null);
  const [blockBusy, setBlockBusy] = useState({});
  const [facts, setFacts] = useState({});
  const [showFacts, setShowFacts] = useState(false);
  const setFact = (k, v) => setFacts((p) => ({ ...p, [k]: v }));
  const [editing, setEditing] = useState({});   // {block_id: bool}
  const [draft, setDraft] = useState({});        // {block_id: text}
  const [prevVer, setPrevVer] = useState({});    // {block_id: version to restore}
  const [resolved, setResolved] = useState({});  // {confirm label: value}
  const [applyBusy, setApplyBusy] = useState(false);
  const [progress, setProgress] = useState(null); // {done,total} while a batch runs
  const [logoData, setLogoData] = useState(null); // uploaded client logo as data-URI
  const cleanFacts = () => Object.fromEntries(Object.entries(facts).filter(([, v]) => (v || "").trim()));

  // Async batch: POST returns a job_id, then poll (avoids the ~100s Cloudflare/ingress request timeout).
  const pollJob = async (jobId) => {
    for (;;) {
      const { data } = await api.get(`/ai/studio/generate-batch/status/${jobId}`);
      setProgress({ done: data.done || 0, total: data.total || 0 });
      if (data.status === "done") return data;
      if (data.status === "error") throw new Error(data.error || "Generation failed");
      await new Promise((r) => setTimeout(r, 2500));
    }
  };
  const runBatch = async (payload) => {
    setProgress({ done: 0, total: (payload.blocks || []).length });
    const { data } = await api.post("/ai/studio/generate-batch", payload);
    if (data.results) return data; // legacy synchronous response
    return await pollJob(data.job_id);
  };
  const [attachProject, setAttachProject] = useState("");
  const [attachName, setAttachName] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [bi, setBi] = useState(null);

  // Digital Brain: prefill Mission from what AIPOS already knows.
  useEffect(() => {
    if (!clientId && !projectId) { setBi(null); return; }
    api.get("/ai/studio/business-intelligence", { params: { client_id: clientId || undefined, project_id: projectId || undefined, generator: "copy" } })
      .then((r) => {
        setBi(r.data);
        if (r.data?.loaded) {
          const fld = r.data.fields || {};
          setF((p) => ({
            ...p,
            context: p.context || fld.context?.value || "",
            industry: (() => { const v = p.industry?.length ? p.industry : fld.industry?.value; return Array.isArray(v) ? v : (typeof v === "string" && v ? v.split(/,\s*/).filter(Boolean) : []); })(),
            goal: (() => { const g = p.goal?.length ? p.goal : fld.goal?.value; return Array.isArray(g) ? g : (typeof g === "string" && g ? g.split(/,\s*/).filter(Boolean) : []); })(),
            audience: (() => { const v = p.audience?.length ? p.audience : fld.audience?.value; return Array.isArray(v) ? v : (typeof v === "string" && v ? v.split(/,\s*/).filter(Boolean) : []); })(),
          }));
          // DNA Auto-Fill: prefill Confirmed Business Facts from Business DNA (only empty fields).
          const cf = fld.advanced?.confirmed_facts?.value;
          if (cf && typeof cf === "object") {
            const KEYMAP = { years_experience: "years_in_business", business_hours: "hours", emergency_service: "emergency", address: "business_address" };
            setFacts((prev) => {
              const next = { ...prev };
              Object.entries(cf).forEach(([k, v]) => { const key = KEYMAP[k] || k; if (String(v || "").trim() && !String(next[key] || "").trim()) next[key] = String(v).trim(); });
              return next;
            });
            if (Object.values(cf).some((v) => String(v || "").trim())) setShowFacts(true);
          }
        }
      }).catch(() => setBi(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, projectId]);

  const toggleBlock = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelected(new Set(blocks.map((b) => b.id)));
  const clearAll = () => setSelected(new Set());

  const chosenCount = mode === "full" ? fullBlocks.length : selected.size;

  const payloadCore = () => {
    const advanced = {};
    ["keywords", "target_city", "target_state"].forEach((k) => { if (f[k]) advanced[k] = f[k]; });
    const industryVal = (f.industry || []).map((x) => isCustom(x) ? (f.industry_custom.trim() || x) : x).filter(Boolean);
    const audienceVal = (f.audience || []).map((x) => isCustom(x) ? (f.audience_custom.trim() || x) : x).filter(Boolean);
    const goalList = (f.goal || []).map((g) => isCustom(g) ? (f.goal_custom.trim() || g) : g).filter(Boolean);
    return {
      context: f.context, industry: industryVal.length ? industryVal.join(", ") : null, goal: goalList.length ? goalList.join(", ") : null,
      audience: audienceVal.length ? audienceVal.join(", ") : null, tone: f.tone, model: f.model, advanced,
      client_id: clientId || null, project_id: projectId || null, confirmed_facts: cleanFacts(),
    };
  };

  const generate = async () => {
    if (!f.context.trim()) { toast.error("Describe your business / mission first"); return; }
    const chosen = mode === "full" ? fullBlocks.map((b) => b.id) : [...selected];
    if (mode === "custom" && chosen.length === 0) { toast.error("Select at least one block"); return; }
    setBusy(true); setResults(null); setGenProjectId(null); setEditing({}); setDraft({}); setPrevVer({}); setResolved({});
    try {
      const data = await runBatch({ mode, blocks: chosen, ...payloadCore() });
      setResults(data.results); setGenProjectId(projectId || null);
      setGenMeta({ cost: data.total_cost_estimated, tokens: data.total_tokens });
      const failed = data.count - data.generated;
      toast.success(`Generated ${data.generated}/${data.count} block${data.count === 1 ? "" : "s"}${failed ? ` · ${failed} failed` : ""}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); setProgress(null); }
  };

  const applyAndRegen = async () => {
    const blockIds = (results || []).map((r) => r.block_id);
    if (!blockIds.length) return;
    // Promote resolved values into structured facts (contact labels map back to their key so reminders clear).
    const labelToKey = Object.fromEntries(CONTACT_KEYS.map(([k, label]) => [label.toLowerCase(), k]));
    const nextFacts = { ...facts };
    Object.entries(resolved).forEach(([label, v]) => { const key = labelToKey[label.toLowerCase()]; if (key && (v || "").trim()) nextFacts[key] = v.trim(); });
    setFacts(nextFacts);
    const merged = Object.fromEntries(Object.entries(nextFacts).filter(([, v]) => (v || "").trim()));
    Object.entries(resolved).forEach(([label, v]) => { if ((v || "").trim() && !(label.toLowerCase() in labelToKey)) merged[label] = v.trim(); });
    setApplyBusy(true);
    try {
      const data = await runBatch({ mode: "custom", blocks: blockIds, ...payloadCore(), confirmed_facts: merged });
      setResults(data.results); setGenMeta({ cost: data.total_cost_estimated, tokens: data.total_tokens });
      setResolved({}); setEditing({}); setDraft({}); setPrevVer({});
      toast.success("Applied confirmed facts & regenerated");
    } catch (e) { toast.error(formatApiError(e)); } finally { setApplyBusy(false); setProgress(null); }
  };

  const setBB = (id, v) => setBlockBusy((p) => ({ ...p, [id]: v }));
  const updateResult = (id, patch) => setResults((rs) => rs.map((r) => (r.block_id === id ? { ...r, ...patch } : r)));

  const regen = async (r) => {
    setBB(r.block_id, "regen");
    try {
      const prev = r.version;
      const { data } = await api.post("/ai/studio/refine", { asset_id: r.asset_id, action: "regenerate", model: f.model });
      setPrevVer((p) => ({ ...p, [r.block_id]: prev }));
      updateResult(r.block_id, { result: data.result, version: data.version, review_status: "edited" });
      toast.success(`Regenerated → v${data.version} · previous kept as v${prev}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBB(r.block_id, null); }
  };
  const startEdit = (r) => { setDraft((p) => ({ ...p, [r.block_id]: r.result })); setEditing((p) => ({ ...p, [r.block_id]: true })); };
  const cancelEdit = (id) => { setEditing((p) => ({ ...p, [id]: false })); setDraft((p) => { const n = { ...p }; delete n[id]; return n; }); };
  const saveEdit = async (r) => {
    const content = draft[r.block_id];
    if (content == null || !content.trim()) { toast.error("Content can't be empty"); return; }
    setBB(r.block_id, "save");
    try {
      const { data } = await api.post(`/ai/studio/assets/${r.asset_id}/edit`, { content });
      updateResult(r.block_id, { result: data.result, version: data.version, review_status: "edited" });
      cancelEdit(r.block_id);
      toast.success(`Saved edit → v${data.version}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBB(r.block_id, null); }
  };
  const restorePrev = async (r) => {
    const v = prevVer[r.block_id]; if (v == null) return;
    setBB(r.block_id, "restore");
    try {
      const { data } = await api.post(`/ai/studio/assets/${r.asset_id}/rollback/${v}`);
      updateResult(r.block_id, { result: data.result, version: data.version, review_status: "edited" });
      setPrevVer((p) => { const n = { ...p }; delete n[r.block_id]; return n; });
      toast.success(`Restored previous content → v${data.version}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBB(r.block_id, null); }
  };
  const approve = async (r) => {
    setBB(r.block_id, "approve");
    try {
      await api.post(`/ai/studio/assets/${r.asset_id}/approve`);
      updateResult(r.block_id, { review_status: "approved" });
      toast.success(`${r.label} approved`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBB(r.block_id, null); }
  };
  const copyOne = async (text) => { try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Copy failed"); } };
  const exportOne = async (r) => {
    try {
      const res = await api.post("/ai/studio/export/docx", { asset_id: r.asset_id }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${r.label.replace(/[^a-z0-9]+/gi, "-")}.docx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const okResults = (results || []).filter((r) => r.ok && r.asset_id);
  const okAssetIds = okResults.map((r) => r.asset_id);
  const combinedText = () => okResults.map((r) => `# ${r.label}\n\n${r.result}`).join("\n\n---\n\n");
  // Facts to Confirm = blank contact details the site needs (deterministic) + every [Confirm: …] the AI emitted.
  const confirmItems = useMemo(() => {
    const map = new Map();
    CONTACT_KEYS.forEach(([k, label]) => { if (!(facts[k] || "").trim()) map.set(label.toLowerCase(), label); });
    (results || []).forEach((r) => {
      if (!r.ok) return;
      const re = /\[confirm:([^\]]*)\]/gi; let m;
      while ((m = re.exec(r.result || ""))) { const label = m[1].trim(); const k = label.toLowerCase(); if (label && !map.has(k)) map.set(k, label); }
    });
    return [...map.values()];
  }, [results, facts]);

  const attachExisting = async () => {
    if (!attachProject) { toast.error("Pick a project"); return; }
    setAttaching(true);
    try { const { data } = await api.post("/ai/studio/batch/attach", { asset_ids: okAssetIds, project_id: attachProject });
      setGenProjectId(data.project_id); toast.success(`Saved ${data.attached} block(s) to project`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setAttaching(false); }
  };
  const attachNew = async () => {
    if (!attachName.trim()) { toast.error("Enter a project name"); return; }
    setAttaching(true);
    try { const { data } = await api.post("/ai/studio/batch/attach", { asset_ids: okAssetIds, new_project_name: attachName, client_id: clientId || null });
      setGenProjectId(data.project_id); toast.success(`Created “${attachName}” & saved ${data.attached} block(s)`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setAttaching(false); }
  };
  const downloadAll = () => {
    const blob = new Blob([combinedText()], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "website-content.md"; a.click(); URL.revokeObjectURL(url);
  };
  const copyAll = async () => { try { await navigator.clipboard.writeText(combinedText()); toast.success("All content copied"); } catch { toast.error("Copy failed"); } };
  const discard = () => { setResults(null); setGenProjectId(null); setGenMeta(null); setPreview(null); setPreviewToken(null); };

  const projectName = (id) => (projects.find((p) => p.id === id) || {}).org_name || "";

  const createPreview = async () => {
    setPvBusy(true);
    try {
      const industryVal = (f.industry || []).map((x) => isCustom(x) ? (f.industry_custom.trim() || x) : x).filter(Boolean);
      const { data } = await api.post("/ai/studio/website-preview", {
        project_id: genProjectId || null,
        business_name: projectName(genProjectId),
        brand_color: f.brand_color || "#C9A227",
        logo: logoData || null,
        industry: industryVal.length ? industryVal.join(", ") : null,
        blocks: okResults.map((r) => ({ block: r.block_id, title: r.title, result: r.result })),
      });
      setPreviewToken(data.token);
      setPreview(`${window.location.origin}/preview/${data.token}`);
      toast.success("Website preview created.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPvBusy(false); }
  };

  const sendPreview = async (overrideEmail) => {
    if (!previewToken) return;
    setSendBusy(true);
    try {
      const { data } = await api.post(`/ai/studio/preview/${previewToken}/send`, { email: overrideEmail || null });
      toast.success(`Preview emailed to ${data.sent_to}.`);
    } catch (e) {
      if (e?.response?.status === 422) {
        setEmailPrompt(true);
      } else { toast.error(formatApiError(e)); }
    } finally { setSendBusy(false); }
  };

  const convertPreview = async () => {
    if (!previewToken) return;
    const pid = genProjectId || projectId;
    if (!pid) { toast.error("Save this content to a project first, then convert."); return; }
    if (!window.confirm(`Use this preview as the starting site for “${projectName(pid) || "this project"}”? This seeds the build — it won't publish a live website.`)) return;
    setConvertBusy(true);
    try {
      const { data } = await api.post(`/ai/studio/preview/${previewToken}/convert`, { project_id: pid });
      toast.success(`Set as starting site for “${data.org_name}” (${data.block_count} blocks).`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setConvertBusy(false); }
  };

  const statusChip = (s) => s === "approved" ? "border-emerald-500/40 text-emerald-300"
    : s === "edited" ? "border-amber-400/40 text-amber-300" : "border-white/15 text-gray-400";

  return (
    <div className="space-y-5" data-testid="website-builder">
      {/* Digital Brain chip */}
      {bi?.loaded && (
        <div className="bg-navy-800 border border-gold/20 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="wb-bi">
          <div className="flex items-center gap-2 text-white font-medium"><Brain size={16} className="text-gold" /> Mission loaded from Business Intelligence
            {bi.company?.org_name && <span className="text-gray-400 text-sm">· {bi.company.org_name}</span>}</div>
          <div className="flex items-center gap-2 text-xs text-gray-500"><span>Knowledge</span>
            <div className="w-24 h-1.5 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-gold" style={{ width: `${bi.knowledge?.score || 0}%` }} /></div>
            <span className="text-gold font-heading">{bi.knowledge?.score || 0}%</span></div>
        </div>
      )}

      {/* MISSION */}
      <div className="bg-navy-800 border border-white/10 p-5 space-y-4" data-testid="wb-mission">
        <div className="flex items-center gap-2 text-white font-medium"><Rocket size={18} className="text-gold" /> Website Content Builder
          <span className="text-gray-500 text-sm font-normal">— one mission, a cohesive website</span></div>

        <ClientProjectPicker clients={clients} projects={projects} clientId={clientId} setClientId={setClientId}
          projectId={projectId} setProjectId={setProjectId} reloadClients={reloadClients} reloadProjects={reloadProjects} />

        <div><label className={LBL}>Mission / business context</label>
          <textarea rows={3} className={SEL} value={f.context} onChange={(e) => set("context", e.target.value)} data-testid="wb-context"
            placeholder="e.g. Grow a family-owned plumbing business in Montgomery, AL. Emergency repairs, installs, water heaters. Trusted, licensed, 20+ yrs." /></div>

        <div className="grid md:grid-cols-4 gap-4">
          <div><label className={LBL}>Industry <span className="text-gray-500 font-normal normal-case">· pick one or more</span></label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-white/15 rounded bg-navy max-h-28 overflow-y-auto" data-testid="wb-industries">
              {config.industries.map((o) => {
                const on = f.industry.includes(o);
                return <button type="button" key={o} onClick={() => toggleArr("industry", o)} data-testid={`wb-industry-${o}`}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${on ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>{o}</button>;
              })}
            </div>
            {f.industry.some(isCustom) && <input className={`${SEL} mt-1.5`} value={f.industry_custom} onChange={(e) => set("industry_custom", e.target.value)} data-testid="wb-industry-custom" placeholder="Describe the industry…" />}
          </div>
          <div className="md:col-span-2"><label className={LBL}>Goals <span className="text-gray-500 font-normal normal-case">· pick one or more</span></label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-white/15 rounded bg-navy max-h-28 overflow-y-auto" data-testid="wb-goals">
              {config.goals.map((o) => {
                const on = f.goal.includes(o);
                return <button type="button" key={o} onClick={() => toggleGoal(o)} data-testid={`wb-goal-${o}`}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${on ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>{o}</button>;
              })}
            </div>
            {f.goal.some(isCustom) && <input className={`${SEL} mt-1.5`} value={f.goal_custom} onChange={(e) => set("goal_custom", e.target.value)} data-testid="wb-goal-custom" placeholder="Describe your custom goal…" />}
          </div>
          <div><label className={LBL}>Audience <span className="text-gray-500 font-normal normal-case">· pick one or more</span></label>
            <div className="flex flex-wrap gap-1.5 p-2 border border-white/15 rounded bg-navy max-h-28 overflow-y-auto" data-testid="wb-audiences">
              {config.audiences.map((o) => {
                const on = f.audience.includes(o);
                return <button type="button" key={o} onClick={() => toggleArr("audience", o)} data-testid={`wb-audience-${o}`}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${on ? "bg-gold text-navy border-gold font-medium" : "border-white/15 text-gray-300 hover:border-gold/50"}`}>{o}</button>;
              })}
            </div>
            {f.audience.some(isCustom) && <input className={`${SEL} mt-1.5`} value={f.audience_custom} onChange={(e) => set("audience_custom", e.target.value)} data-testid="wb-audience-custom" placeholder="Describe the audience…" />}
          </div>
          <div><label className={LBL}>Tone</label>
            <select className={SEL} value={f.tone} onChange={(e) => set("tone", e.target.value)} data-testid="wb-tone">
              {config.tones.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></div>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div><label className={LBL}>Keywords</label><input className={SEL} value={f.keywords} onChange={(e) => set("keywords", e.target.value)} data-testid="wb-keywords" placeholder="plumbing, water heater" /></div>
          <div><label className={LBL}>Target city</label><input className={SEL} value={f.target_city} onChange={(e) => set("target_city", e.target.value)} data-testid="wb-city" placeholder="Montgomery" /></div>
          <div><label className={LBL}>Target state</label><input className={SEL} value={f.target_state} onChange={(e) => set("target_state", e.target.value)} data-testid="wb-state" placeholder="AL" /></div>
          <div><label className={LBL}>AI model</label>
            <select className={SEL} value={f.model} onChange={(e) => set("model", e.target.value)} data-testid="wb-model">
              {config.models.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select></div>
        </div>

        {/* CONFIRMED BUSINESS FACTS — optional. Blank = unknown (never assumed "No"). */}
        <div className="border border-white/10" data-testid="wb-facts">
          <button type="button" onClick={() => setShowFacts((v) => !v)} data-testid="wb-facts-toggle"
            className="w-full flex items-center justify-between px-4 py-2.5 text-left">
            <span className="text-xs uppercase tracking-wide text-gray-400 flex items-center gap-2">
              <ShieldCheck size={14} className="text-gold" /> Confirmed Business Facts
              <span className="text-gray-600 normal-case">· optional — only fill what's true; blank = unknown</span></span>
            <ChevronDown className={`transition-transform ${showFacts ? "rotate-180" : ""}`} size={16} />
          </button>
          {showFacts && (
            <div className="p-4 pt-0 grid sm:grid-cols-2 gap-3" data-testid="wb-facts-fields">
              {TRUST_FIELDS.map(([k, label, ph]) => (
                <div key={k}>
                  <label className="block text-[11px] text-gray-500 mb-1">{label}</label>
                  <input className={SEL} value={facts[k] || ""} onChange={(e) => setFact(k, e.target.value)} placeholder={ph} data-testid={`wb-fact-${k}`} />
                </div>
              ))}
              <div className="sm:col-span-2 text-[11px] text-gray-500">
                Leave blank if unknown — AIPOS flags it as <span className="text-amber-300">[Confirm: …]</span> instead of guessing.
                Confirmed facts are stated consistently across the whole site.
              </div>
            </div>
          )}
        </div>

        {/* CONTACT DETAILS — the site NEEDS these. Provided → stated; blank → flagged [Confirm] to fill before publishing. */}
        <div className="border border-white/10 p-4" data-testid="wb-contact-details">
          <div className="text-xs uppercase tracking-wide text-gray-400 flex items-center gap-2 mb-3">
            <ShieldCheck size={14} className="text-gold" /> Contact Details
            <span className="text-gray-600 normal-case">· shown on the site — blank ones become <span className="text-amber-300">[Confirm: …]</span> to fill before publishing</span>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div><label className="block text-[11px] text-gray-500 mb-1">Phone</label>
              <input className={SEL} value={facts.phone || ""} onChange={(e) => setFact("phone", e.target.value)} placeholder="e.g. (334) 555-0182" data-testid="wb-fact-phone" /></div>
            <div><label className="block text-[11px] text-gray-500 mb-1">Email</label>
              <input className={SEL} value={facts.email || ""} onChange={(e) => setFact("email", e.target.value)} placeholder="e.g. service@rivercityplumbing.com" data-testid="wb-fact-email" /></div>
            <div><label className="block text-[11px] text-gray-500 mb-1">Business address</label>
              <input className={SEL} value={facts.business_address || ""} onChange={(e) => setFact("business_address", e.target.value)} placeholder="e.g. 123 Dexter Ave, Montgomery, AL" data-testid="wb-fact-business_address" /></div>
          </div>
        </div>

        {/* MODE */}
        <div className="grid sm:grid-cols-2 gap-3">
          <button onClick={() => setMode("full")} data-testid="wb-mode-full"
            className={`flex items-start gap-3 p-4 border text-left transition-colors ${mode === "full" ? "border-gold/60 bg-gold/10" : "border-white/10 bg-navy hover:border-white/25"}`}>
            <Rocket size={18} className="text-gold mt-0.5" />
            <div><div className="text-white text-sm font-medium">Full Website</div>
              <div className="text-gray-500 text-xs mt-0.5">One click → {fullBlocks.length} blocks: hero, about, services, pages, CTA, FAQ, SEO, sitemap, brand voice.</div></div>
          </button>
          <button onClick={() => setMode("custom")} data-testid="wb-mode-custom"
            className={`flex items-start gap-3 p-4 border text-left transition-colors ${mode === "custom" ? "border-gold/60 bg-gold/10" : "border-white/10 bg-navy hover:border-white/25"}`}>
            <Target size={18} className="text-gold mt-0.5" />
            <div><div className="text-white text-sm font-medium">Custom Build</div>
              <div className="text-gray-500 text-xs mt-0.5">Pick exactly the blocks you need and generate only those.</div></div>
          </button>
        </div>

        {mode === "custom" && (
          <div className="border border-white/10 p-4" data-testid="wb-custom-picker">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs uppercase tracking-wide text-gray-500">Choose blocks ({selected.size})</span>
              <div className="flex gap-2"><button onClick={selectAll} data-testid="wb-select-all" className="text-xs text-gold hover:text-gold-light">Select all</button>
                <span className="text-gray-700">·</span><button onClick={clearAll} data-testid="wb-clear" className="text-xs text-gray-400 hover:text-white">Clear</button></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {blocks.map((b) => (
                <label key={b.id} data-testid={`wb-block-${b.id}`}
                  className={`flex items-center gap-2 px-3 py-2 border text-sm cursor-pointer transition-colors ${selected.has(b.id) ? "border-gold/50 bg-gold/10 text-white" : "border-white/10 text-gray-300 hover:border-white/25"}`}>
                  <input type="checkbox" className="accent-gold" checked={selected.has(b.id)} onChange={() => toggleBlock(b.id)} />
                  <span>{b.emoji} {b.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <button onClick={generate} disabled={busy} data-testid="wb-generate-btn"
          className="w-full flex items-center justify-center gap-2 bg-gold text-navy py-3 font-semibold hover:bg-gold-light transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={17} /> : <Sparkles size={17} />}
          {busy ? (progress && progress.total ? `Generating… ${progress.done}/${progress.total} blocks` : "Generating website content…") : `Generate ${mode === "full" ? "Full Website" : `${chosenCount} block${chosenCount === 1 ? "" : "s"}`}`}
        </button>
      </div>

      {busy && <div className="text-center text-gray-500 text-sm" data-testid="wb-generating-note">Generating {chosenCount} block{chosenCount === 1 ? "" : "s"} with one shared mission — a full site can take up to a minute or two, and you can safely leave this running.</div>}

      {/* RESULTS */}
      {results && (
        <div className="space-y-4" data-testid="wb-results">
          {genMeta && (genMeta.cost != null || genMeta.tokens != null) && (
            <div className="flex items-center gap-2 text-xs" data-testid="wb-gen-cost">
              <span className="px-2.5 py-1 border border-gold/30 text-gold rounded-full">Generation cost: ${Number(genMeta.cost || 0).toFixed(4)} <span className="text-gray-500">est</span></span>
              {genMeta.tokens != null && <span className="px-2.5 py-1 border border-white/15 text-gray-300 rounded-full">{genMeta.tokens.toLocaleString()} tokens</span>}
            </div>
          )}
          {okResults.length > 0 && (
            <div className="bg-navy-800 border border-gold/20 p-4 space-y-2" data-testid="wb-preview-box">
              <div className="flex flex-wrap items-center gap-4 pb-3 mb-1 border-b border-white/10" data-testid="wb-branding">
                <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                  {logoData ? <img src={logoData} alt="logo" className="h-8 w-8 object-contain bg-white/10 rounded" /> : <span className="h-8 w-8 rounded bg-white/5 border border-dashed border-white/25 flex items-center justify-center text-gray-500 text-[9px]">LOGO</span>}
                  <input type="file" accept="image/*" className="hidden" data-testid="wb-logo-upload"
                    onChange={(e) => { const file = e.target.files?.[0]; if (!file) return; if (file.size > 800000) { toast.error("Logo too large (max ~800KB)"); return; } const rd = new FileReader(); rd.onload = () => setLogoData(rd.result); rd.readAsDataURL(file); }} />
                  <span className="underline decoration-dotted">{logoData ? "Change logo" : "Upload client logo"}</span>
                </label>
                {logoData && <button onClick={() => setLogoData(null)} data-testid="wb-logo-clear" className="text-[11px] text-red-300 hover:text-red-200">Remove</button>}
                {!logoData && <span className="text-[11px] text-gray-500">No logo? A branded logo is a great upsell — the mockup uses a clean lettermark until one's added.</span>}
                <label className="flex items-center gap-2 text-xs text-gray-300 ml-auto">Brand color
                  <input type="color" value={f.brand_color} onChange={(e) => set("brand_color", e.target.value)} data-testid="wb-brand-color" className="h-7 w-10 bg-transparent border border-white/15 rounded cursor-pointer" />
                </label>
              </div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-white text-sm font-medium flex items-center gap-2"><Globe size={15} className="text-gold" /> Turn this into a shareable website preview</div>
                <button onClick={createPreview} disabled={pvBusy} data-testid="wb-create-preview"
                  className="inline-flex items-center gap-1.5 bg-gold text-navy px-4 py-2 text-sm font-semibold hover:bg-gold-light disabled:opacity-50">
                  {pvBusy ? <Loader2 size={14} className="animate-spin" /> : <Globe size={14} />} {preview ? "Rebuild preview" : "Create website preview"}
                </button>
              </div>
              {preview && (
                <div className="flex items-center gap-2 bg-navy border border-white/10 p-2.5">
                  <code className="text-xs text-gold truncate flex-1" data-testid="wb-preview-link">{preview}</code>
                  <button onClick={() => { navigator.clipboard.writeText(preview); toast.success("Preview link copied."); }} className="text-gray-400 hover:text-white" title="Copy"><Copy size={14} /></button>
                  <a href={preview} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white" title="Open"><ExternalLink size={14} /></a>
                </div>
              )}
              {preview && (
                <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="wb-preview-actions">
                  <button onClick={() => sendPreview()} disabled={sendBusy} data-testid="wb-send-preview"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">
                    {sendBusy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send to client
                  </button>
                  <button onClick={convertPreview} disabled={convertBusy || !(genProjectId || projectId)}
                    title={!(genProjectId || projectId) ? "Save this content to a project first" : "Use as the project's starting site"}
                    data-testid="wb-convert-preview"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-40">
                    {convertBusy ? <Loader2 size={13} className="animate-spin" /> : <FolderInput size={13} />} Convert to project
                  </button>
                  <span className="text-[11px] text-gray-500">Email the preview or set it as the build's starting content.</span>
                </div>
              )}
              {previewToken && <PreviewManager token={previewToken} projectId={genProjectId || projectId} />}
            </div>
          )}
          {confirmItems.length > 0 && (
            <div className="bg-navy-800 border border-amber-400/30 p-4 space-y-3" data-testid="wb-confirm-checklist">
              <div className="flex items-center gap-2 text-amber-200 font-medium text-sm">
                <ListChecks size={16} /> Facts to Confirm Before Publishing — {confirmItems.length}</div>
              <div className="text-[11px] text-gray-400">
                These claims aren't in your brief, so AIPOS flagged them instead of inventing. Enter the real value and
                Apply to use it across the site — or fill “Confirmed Business Facts” above and regenerate. The inline
                <span className="text-amber-300"> [Confirm: …]</span> markers stay until resolved, so nothing unverified ships by accident.
              </div>
              <div className="space-y-2">
                {confirmItems.map((label) => (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={`wb-confirm-row-${slug(label)}`}>
                    <span className="text-xs text-amber-100 min-w-[160px] flex-shrink-0">{label}</span>
                    <input className="flex-1 min-w-[180px] bg-navy border border-white/15 text-gray-100 p-2 text-xs focus:border-gold focus:outline-none"
                      placeholder="Enter the confirmed value…" value={resolved[label] || ""}
                      onChange={(e) => setResolved((p) => ({ ...p, [label]: e.target.value }))}
                      data-testid={`wb-confirm-input-${slug(label)}`} />
                  </div>
                ))}
              </div>
              <button onClick={applyAndRegen} disabled={applyBusy} data-testid="wb-apply-facts"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium bg-gold text-navy hover:bg-gold-light disabled:opacity-50">
                {applyBusy ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Apply confirmed facts &amp; regenerate
              </button>
            </div>
          )}
          {/* Save bar */}
          {genProjectId ? (
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/25 px-4 py-3 text-emerald-300 text-sm" data-testid="wb-autosaved">
              <CheckCircle2 size={16} /> Saved to project. All approved & generated blocks are attached.
            </div>
          ) : okResults.length > 0 && (
            <div className="bg-navy-800 border border-gold/20 p-4 space-y-3" data-testid="wb-save-bar">
              <div className="text-white text-sm font-medium">Where would you like to save this content?</div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="flex gap-2">
                  <select className={SEL} value={attachProject} onChange={(e) => setAttachProject(e.target.value)} data-testid="wb-attach-project">
                    <option value="">Save to existing project…</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.org_name}</option>)}
                  </select>
                  <button onClick={attachExisting} disabled={attaching} data-testid="wb-attach-existing-btn"
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50"><Save size={13} /> Save</button>
                </div>
                <div className="flex gap-2">
                  <input className={SEL} value={attachName} onChange={(e) => setAttachName(e.target.value)} placeholder="…or create a new project" data-testid="wb-new-project-name" />
                  <button onClick={attachNew} disabled={attaching} data-testid="wb-attach-new-btn"
                    className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-medium border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50"><FolderPlus size={13} /> Create</button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={downloadAll} data-testid="wb-download-all" className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><Download size={13} /> Download all</button>
                <button onClick={copyAll} data-testid="wb-copy-all" className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><Copy size={13} /> Copy all</button>
                <button onClick={discard} data-testid="wb-discard" className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-red-500/30 text-red-300 hover:bg-red-500/10 ml-auto"><Trash2 size={13} /> Discard</button>
              </div>
            </div>
          )}

          {results.map((r) => (
            <div key={r.block_id} className={`bg-navy-800 border p-5 space-y-3 ${r.ok ? "border-gold/25" : "border-red-500/30"}`} data-testid={`wb-result-${r.block_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-white font-medium">{r.emoji} {r.label}</div>
                {r.ok ? (
                  <div className="flex items-center gap-2 text-xs text-gray-400 flex-wrap">
                    <span className="px-2 py-0.5 border border-white/15 rounded-full">v{r.version}</span>
                    <span className="px-2 py-0.5 border border-white/15 rounded-full">{r.provider} · {r.model?.split("/").pop()}</span>
                    <span className={`px-2 py-0.5 border rounded-full capitalize ${statusChip(r.review_status)}`}>{r.review_status}</span>
                  </div>
                ) : <span className="flex items-center gap-1 text-red-300 text-xs"><AlertTriangle size={13} /> {r.error || "Failed"}</span>}
              </div>
              {r.ok && (
                <>
                  <textarea
                    value={editing[r.block_id] ? (draft[r.block_id] ?? "") : r.result}
                    onChange={(e) => { if (editing[r.block_id]) setDraft((p) => ({ ...p, [r.block_id]: e.target.value })); }}
                    readOnly={!editing[r.block_id]} rows={8}
                    data-testid={`wb-result-text-${r.block_id}`}
                    className={`w-full bg-navy border p-3 text-sm leading-relaxed focus:outline-none font-mono ${editing[r.block_id] ? "border-gold text-white" : "border-white/15 text-gray-100"}`} />
                  <div className="flex flex-wrap gap-2">
                    {editing[r.block_id] ? (
                      <>
                        <button onClick={() => saveEdit(r)} disabled={blockBusy[r.block_id] === "save"} data-testid={`wb-save-${r.block_id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gold text-navy hover:bg-gold-light disabled:opacity-50">
                          {blockBusy[r.block_id] === "save" ? <Loader2 className="animate-spin" size={13} /> : <Save size={13} />} Save Changes</button>
                        <button onClick={() => cancelEdit(r.block_id)} data-testid={`wb-cancel-${r.block_id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-300 hover:border-white/30"><X size={13} /> Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(r)} data-testid={`wb-edit-${r.block_id}`}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><Pencil size={13} /> Edit</button>
                        <button onClick={() => regen(r)} disabled={!!blockBusy[r.block_id]} data-testid={`wb-regen-${r.block_id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50 disabled:opacity-50">{blockBusy[r.block_id] === "regen" ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} Regenerate</button>
                        {prevVer[r.block_id] != null && (
                          <button onClick={() => restorePrev(r)} disabled={!!blockBusy[r.block_id]} data-testid={`wb-restore-${r.block_id}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 disabled:opacity-50">{blockBusy[r.block_id] === "restore" ? <Loader2 className="animate-spin" size={13} /> : <Undo2 size={13} />} Restore previous</button>
                        )}
                        <button onClick={() => approve(r)} disabled={!!blockBusy[r.block_id] || r.review_status === "approved"} data-testid={`wb-approve-${r.block_id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-gold/50 text-gold hover:bg-gold/10 disabled:opacity-50">{blockBusy[r.block_id] === "approve" ? <Loader2 className="animate-spin" size={13} /> : <CheckCircle2 size={13} />} {r.review_status === "approved" ? "Approved" : "Approve"}</button>
                        <button onClick={() => copyOne(r.result)} data-testid={`wb-copy-${r.block_id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><Copy size={13} /> Copy</button>
                        <button onClick={() => exportOne(r)} data-testid={`wb-export-${r.block_id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-white/15 text-gray-200 hover:border-gold/50"><FileDown size={13} /> Word</button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      <PromptDialog open={emailPrompt} title="Send preview" label="No client email on file — enter an address to send the preview to"
        placeholder="client@example.com" confirmText="Send preview"
        onSubmit={(email) => { setEmailPrompt(false); sendPreview(email); }} onClose={() => setEmailPrompt(false)} />
    </div>
  );
}
